import logo from "assets/logo_verify.svg";
import { Box, Typography, Grid, Button } from "@mui/material";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { object, string, TypeOf } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useResetPasswordMutation } from "../../redux/api/authApi";
import { showErrorToast, showSuccessToast } from "components/Toast/Toast";
import { LoadingButton } from "@mui/lab";
import home from "assets/img-login-admin.jpg";
import FormInputPassword from "components/FormInputPassword";
import { useIntl } from "react-intl";


const resetPasswordSchema = object({
  password: string()
    .min(1, "La contraseña es obligatoria")
    .min(8, "La contraseña debe tener al menos 8 caracteres"),
  passwordConfirm: string().min(1, "Introduce la nueva contraseña"),
}).refine((data) => data.password === data.passwordConfirm, {
  message: "Las contraseñas no coinciden",
  path: ["passwordConfirm"],
});

type ResetPasswordInput = TypeOf<typeof resetPasswordSchema>;

export const ResetPasswordPage = () => {
  const { formatMessage } = useIntl();
  const { resetToken } = useParams<{ resetToken: string }>();

  const methods = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
  });

  // 👇 API Login Mutation
  const [resetPassword, { isLoading, isError, error, isSuccess }] =
    useResetPasswordMutation();

  const navigate = useNavigate();

  const {
    reset,
    handleSubmit,
    formState: { isSubmitSuccessful },
  } = methods;

  useEffect(() => {
    if (isError) {
      if (Array.isArray((error as any).data.error)) {
        (error as any).data.error.forEach((el: any) =>
          showErrorToast(el.message)
        );
      } else {
        showErrorToast((error as any).data.message);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const navigateToLogin = () => {
    navigate("/login");
  };

  const onSubmitHandler: SubmitHandler<ResetPasswordInput> = (values) => {
    resetPassword({ ...values, resetToken: resetToken! });
  };

  if (isSuccess) {
    return (
      <Grid
        container
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "100%",
          height: "100%"
        }}
      >
        <Grid item md={7} sx={{
          height: "100%",
          minHeight: "100%",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundImage: `url(${home})`,
          display: { xs: "none", md: "flex" }
        }}>
        </Grid>
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            maxWidth: "27rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            minHeight: "100%",
            backgroundImage: `url()`,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            paddingX: {
              xs: "2rem", // Para pantallas pequeñas (móviles)
              md: "4rem", // Para desktops y pantallas más grandes
            }
          }}
        >
          <Box sx={{
            maxWidth: "27rem"
          }}
            display="flex"
            alignItems={"center"}
            justifyContent={"center"}
            flexDirection={"column"}
          >
            <Box
              sx={{
                margin: {
                  xs: "2rem 1.625rem", // Margen superior para teléfonos móviles
                  sm: "2rem", // Margen superior para tablets
                  md: "2rem", // Margen superior para desktop
                  lg: "1.5rem", // Margen superior para desktop
                  xl: "3rem", // Margen superior para desktop
                  // Puedes agregar más breakpoints si es necesario
                },
                height: {
                  xs: "5.25rem", // Altura para teléfonos móviles
                  sm: "80px", // Altura para tablets
                  md: "100px", // Altura para desktop
                  // Puedes agregar más breakpoints si es necesario
                },
              }}
            >
              <img src={logo} style={{ height: '100%', width: 'auto' }} alt="Logo" />
            </Box>
            <Typography
              textAlign="left"
              component="h1"
              color="secondary"
              sx={{
                width: "100%",
                fontWeight: 600,
                fontSize: { xs: "1.5rem", md: "2rem" },
                lineHeight: "1.813rem",
              }}
            >
              {formatMessage({ id: "reset.password.title" })}              
            </Typography>
            <Box sx={{ width: "100%" }}>
              <Typography
                textAlign="left"
                component="h2"
                sx={{
                  width: "100%",
                  fontWeight: 400,
                  fontSize: { xs: "1rem" },
                  marginBottom: "1.2rem",
                  marginTop: "1rem",
                }}
              >
                {formatMessage({ id: "reset.password.success" })} 
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                sx={{
                  width: "100%",
                }}
                onClick={navigateToLogin}>
                {formatMessage({ id: "reset.password.back" })} 
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    )
  }





  return (
    <Grid
      container
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        minHeight: "100%",
        height: "100%"
      }}
    >
      <Grid item md={7} sx={{
        height: "100%",
        minHeight: "100%",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundImage: `url(${home})`,
        display: { xs: "none", md: "flex" }
      }}>
      </Grid>
      <Grid
        item
        xs={12}
        md={5}
        sx={{
          maxWidth: "27rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          minHeight: "100%",
          backgroundImage: `url()`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          paddingX: {
            xs: "2rem", // Para pantallas pequeñas (móviles)
            md: "4rem", // Para desktops y pantallas más grandes
          }
        }}
      >
        <Box sx={{
          maxWidth: "27rem"
        }}
          display="flex"
          alignItems={"center"}
          justifyContent={"center"}
          flexDirection={"column"}
        >
          <Box
            sx={{
              margin: {
                xs: "2rem 1.625rem", // Margen superior para teléfonos móviles
                sm: "2rem", // Margen superior para tablets
                md: "2rem", // Margen superior para desktop
                lg: "1.5rem", // Margen superior para desktop
                xl: "3rem", // Margen superior para desktop
                // Puedes agregar más breakpoints si es necesario
              },
              height: {
                xs: "5.25rem", // Altura para teléfonos móviles
                sm: "80px", // Altura para tablets
                md: "100px", // Altura para desktop
                // Puedes agregar más breakpoints si es necesario
              },
            }}
          >
            <img src={logo} style={{ height: '100%', width: 'auto' }} alt="Logo" />
          </Box>
          <Typography
            textAlign="left"
            component="h1"
            color="secondary"
            sx={{
              width: "100%",
              fontWeight: 600,
              fontSize: { xs: "1.5rem", md: "2rem" },
              lineHeight: "1.813rem",
            }}
          >
            {formatMessage({ id: "reset.password.title" })}
          </Typography>
          <Typography
            textAlign="left"
            component="span"
            sx={{
              width: "100%",
              fontWeight: 400,
              fontSize: { xs: "1rem" },
              marginBottom: "2rem",
              marginTop: "1rem",
            }}
          >
            {formatMessage({ id: "reset.password.instructions" })} 
          </Typography>
          <FormProvider {...methods}>
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmitHandler)}
              noValidate
              autoComplete="off"
              maxWidth="27rem"
              width="100%"
            >
              <FormInputPassword
                name="password"
                label="Nueva contraseña"
                autoComplete="new-password"
              />
              <FormInputPassword
                name="passwordConfirm"
                label="Repite la contraseña"
                autoComplete="new-password"
              />
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1rem",
                }}
              >
                <LoadingButton
                  variant="contained"
                  color="secondary"
                  sx={{
                    width: "100%",
                    marginTop: "1rem",
                  }}
                  disableElevation
                  type="submit"
                  loading={isLoading}
                >
                  {formatMessage({ id: "reset.password.save" })} 
                </LoadingButton>
              </Box>
            </Box>
          </FormProvider>
        </Box>
      </Grid>
    </Grid>
  );
};